/**
 * 분류 사례연구 (?page=cases) — 상품 사실과 원문 조건을 설명하는 참고 사례.
 * 게시 번호는 조건부 참고이며 현재 엔진의 실행 결과와 구분한다.
 */
import React, { useEffect, useState } from "react";
import ApiPortalLayout, { SideTocItem } from "./ApiPortalLayout";

const fmtNat = (c) => {
  const d = String(c || "").replace(/\D/g, "");
  return d.length === 10 ? d.replace(/^(\d{4})(\d{2})(\d{4})$/, "$1.$2-$3") : d;
};

const S = {
  page: { maxWidth: 880, margin: "0 auto", padding: "28px 20px 60px" },
  h1: { fontSize: 24, fontWeight: 800, margin: "0 0 6px", color: "#111827" },
  p: { fontSize: 14, color: "#4b5563", lineHeight: 1.7, margin: "0 0 20px" },
  card: {
    background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12,
    padding: "20px 22px", marginBottom: 16,
    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
  },
};

export default function CaseStudiesPage({ onNavigate }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    fetch("/api/tools/hs-cases")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setItems(d?.items || []))
      .catch(() => setItems([]));
  }, []);

  return (
    <ApiPortalLayout
      active="cases"
      onNavigate={onNavigate}
      toc={(items || []).map((c, i) => (
        <SideTocItem key={c.slug} href={`#case-${c.slug}`}>
          {String(i + 1).padStart(2, "0")}. {c.title}
        </SideTocItem>
      ))}
    >
      <div style={S.page}>
        <h1 style={S.h1}>분류 사례연구 — 이 세번, 왜?</h1>
        <p style={S.p}>
          이름만 보면 틀리기 쉬운 <b>분류 쟁점</b>을 상품 확인 질문과
          <b> 관세율표 류 주(法的 주석)·HS 해설서</b>로 설명하는 학습·참고 자료입니다.
          아래 번호와 해설은 게시된 사례 내용이며, 현재 PROVE의 실시간 실행 결과가 아닙니다.
          실제 입력에서는 확인한 상품 정보와 구현된 검토 범위에 따라 추가 질문이나 보류가 나올 수 있습니다.
        </p>

        {items === null && (
          <div style={{ fontSize: 13, color: "#6b7280" }}>불러오는 중…</div>
        )}
        {Array.isArray(items) && items.length === 0 && (
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            게시된 사례가 없습니다.
          </div>
        )}
        {(items || []).map((c, i) => (
          <div key={c.slug} id={`case-${c.slug}`} style={{ ...S.card, scrollMarginTop: 72 }}>
            <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 4 }}>
              CASE {String(i + 1).padStart(2, "0")}
            </div>
            <div
              style={{
                fontSize: 17, fontWeight: 800, color: "#111827",
                marginBottom: 10, lineHeight: 1.4,
              }}
            >
              {c.title}
            </div>

            {/* 게시된 참고 사례 — 실행 결과가 아님 */}
            <div
              style={{
                display: "flex", gap: 10, flexWrap: "wrap",
                alignItems: "center", marginBottom: 12,
              }}
            >
              <span style={{ fontSize: 13, color: "#4b5563" }}>
                품명: <b>{c.item}</b>
              </span>
              {c.wrong_code && (
                <span
                  style={{
                    fontSize: 13, fontFamily: "ui-monospace,monospace",
                    color: "#475569", background: "#f1f5f9",
                    padding: "2px 9px", borderRadius: 6,
                  }}
                  title="비교할 분류 경로"
                >
                  비교 {fmtNat(c.wrong_code)}
                </span>
              )}
              <span
                style={{
                  fontSize: 13.5, fontWeight: 700,
                  fontFamily: "ui-monospace,monospace",
                  color: "#1e40af", background: "#eff6ff",
                  padding: "2px 9px", borderRadius: 6,
                }}
              >
                조건 확인 후 검토 {fmtNat(c.correct_code)}
              </span>
            </div>

            <div
              style={{
                fontSize: 13.5, color: "#374151", lineHeight: 1.75,
                marginBottom: 12,
              }}
            >
              {c.trap}
            </div>

            {c.question && (
              <div
                style={{
                  fontSize: 13, color: "#664d03", background: "#fef7e0",
                  border: "1px solid #f9ab00", borderRadius: 8,
                  padding: "9px 12px", marginBottom: 10,
                }}
              >
                사례의 상품 확인 질문: <b>{c.question}</b>
              </div>
            )}

            <div
              style={{
                borderLeft: "3px solid #1a73e8", background: "#f8fafc",
                borderRadius: "0 8px 8px 0", padding: "10px 14px",
                marginBottom: 8,
              }}
            >
              <div
                style={{
                  fontSize: 12, fontWeight: 700, color: "#1a73e8",
                  marginBottom: 4,
                }}
              >
                근거 요지 — {c.basis_label}
              </div>
              <div
                style={{
                  fontSize: 13, color: "#374151", lineHeight: 1.75,
                  fontStyle: "italic",
                }}
              >
                {c.basis_quote}
              </div>
              {c.basis_where && (
                <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 5 }}>
                  {c.basis_where}
                </div>
              )}
            </div>
          </div>
        ))}

        <div
          style={{
            marginTop: 8, fontSize: 12, color: "#664d03",
            background: "#fef7e0", borderRadius: 8, padding: "10px 14px",
            lineHeight: 1.7,
          }}
        >
          ⚠ 본 사례는 관세율표 류 주·HS 해설서(관세법령정보포털 공개 자료)를
          근거로 한 <b>학습·참고용</b> 자료이며 법적 효력이 없습니다. 실제
          수출입 물품의 세번은 재질·구성·용도에 따라 달라질 수 있으므로, 최종
          확정이 필요한 경우 관세평가분류원 <b>품목분류 사전심사</b>를
          이용하시기 바랍니다.
        </div>
      </div>
    </ApiPortalLayout>
  );
}
